'use client';

/**
 * Tools layout that automatically tracks tool usage
 * and injects WebApplication JSON-LD structured data for each tool page
 */

import { useEffect, useMemo } from 'react';
import { usePathname } from 'next/navigation';
import { trackToolUsage } from '@/components/RecentlyUsedTools';
import { getAllTools } from '@/lib/tools-data';

const SITE_URL = 'https://theartofcto.com';

export default function ToolsLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  const pathname = usePathname();

  // Track tool usage when navigating to a tool page
  useEffect(() => {
    // Only track if we're on an actual tool page (not the /tools listing page)
    if (pathname && pathname !== '/tools' && pathname !== '/tools/pathways') {
      trackToolUsage(pathname);
    }
  }, [pathname]);

  // Generate WebApplication JSON-LD for individual tool pages
  const toolJsonLd = useMemo(() => {
    if (!pathname || pathname === '/tools' || pathname === '/tools/pathways') return null;

    const allTools = getAllTools();
    const tool = allTools.find(t => t.href === pathname);
    if (!tool) return null;

    return {
      '@context': 'https://schema.org',
      '@type': 'WebApplication',
      name: tool.title,
      description: tool.description,
      url: `${SITE_URL}${tool.href}`,
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web Browser',
      browserRequirements: 'Requires JavaScript',
      offers: {
        '@type': 'Offer',
        price: tool.badge === 'PREMIUM' ? '19.99' : '0',
        priceCurrency: 'USD',
      },
      author: {
        '@type': 'Organization',
        name: 'The Art of CTO',
        url: SITE_URL,
      },
      provider: {
        '@type': 'Organization',
        name: 'The Art of CTO',
        url: SITE_URL,
      },
    };
  }, [pathname]);

  return (
    <>
      {toolJsonLd && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(toolJsonLd) }}
        />
      )}
      {children}
    </>
  );
}
